import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminService } from '../../services/admin.service';
import { ScrapeRun, ScrapeStatus } from '../../models';

@Component({
  selector: 'app-admin-scrape',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="container page">
      <div class="admin-hero glass">
        <div>
          <span class="eyeline">Operations</span>
          <h1>Scrape command center</h1>
          <p class="muted">Monitor live scraper health, recent logs, and trigger a fresh full scrape when needed.</p>
        </div>
        <div class="hero-actions">
          <button class="btn" [disabled]="running" (click)="run()">{{ running ? 'Running scrape...' : 'Run scrape' }}</button>
          <button class="btn danger-btn" *ngIf="running" [disabled]="stopping" (click)="stop()">
            {{ stopping ? 'Stopping...' : 'Stop scrape' }}
          </button>
        </div>
      </div>

      <div *ngIf="notice" class="notice-state">{{ notice }}</div>
      <div *ngIf="error" class="error-state">{{ error }}</div>
      <div class="progress-card glass" *ngIf="status?.progress as progress">
        <div class="progress-head">
          <div>
            <span class="eyeline">Live run</span>
            <h2>{{ progress.status === 'running' ? 'Scrape running' : progress.status === 'cancelled' ? 'Scrape stopped' : 'Scrape progress' }}</h2>
            <p>{{ progress.lastMessage }}</p>
          </div>
          <strong>{{ progressPercent(progress) }}%</strong>
        </div>
        <div class="progress-track" aria-label="Scrape progress">
          <span [style.width.%]="progressPercent(progress)"></span>
        </div>
        <div class="progress-grid">
          <span><strong>{{ progress.completed }}</strong> completed</span>
          <span><strong>{{ progress.total }}</strong> total</span>
          <span><strong>{{ progress.recordsFound }}</strong> rows found</span>
          <span><strong>{{ progress.failedCount }}</strong> failed</span>
        </div>
        <div class="current-cinema" *ngIf="progress.status === 'running'">
          Now scraping: <strong>{{ progress.currentCinemaName || 'Preparing next cinema' }}</strong>
          <span *ngIf="progress.currentChain">({{ progress.currentChain }})</span>
        </div>
      </div>
      <div class="stats">
        <div class="glass stat">
          <span>Active showtimes</span>
          <strong>{{ status?.activeShowtimeCount || 0 }}</strong>
        </div>
        <div class="glass stat">
          <span>Failure count</span>
          <strong>{{ status?.failureCount || 0 }}</strong>
        </div>
        <div class="glass stat">
          <span>Total logs loaded</span>
          <strong>{{ logs.length }}</strong>
        </div>
      </div>

      <div class="section-head">
        <div>
          <span class="eyeline">Recent activity</span>
          <h2 class="section-title">Scrape sessions</h2>
        </div>
      </div>

      <div class="run-list" *ngIf="runs.length">
        <button
          type="button"
          class="run-chip"
          *ngFor="let run of runs"
          [class.active]="run.id === selectedRunId"
          (click)="selectRun(run.id)"
        >
          <strong>{{ runLabel(run) }}</strong>
          <span>{{ run.status }} · {{ run.completedCinemas }}/{{ run.totalCinemas }} · {{ run.recordsFound }} rows</span>
        </button>
      </div>

      <div class="section-head compact-head">
        <div>
          <span class="eyeline">Selected session</span>
          <h2 class="section-title">Scrape logs</h2>
        </div>
      </div>

      <div *ngIf="loading" class="skeleton"></div>
      <div class="logs glass" *ngIf="!loading && logs.length">
        <div class="log-row header">
          <span>Scraper</span>
          <span>Status</span>
          <span>Records</span>
          <span>Message</span>
        </div>
        <div class="log-row" *ngFor="let log of logs">
          <strong><span class="mobile-label">Scraper</span>{{ value(log, 'scraperName') }}</strong>
          <span class="status" [class]="statusClass(value(log, 'status'))">{{ value(log, 'status') }}</span>
          <span><span class="mobile-label">Records</span>{{ value(log, 'recordsFound') || '0' }}</span>
          <small><span class="mobile-label">Message</span>{{ value(log, 'message') || 'No message' }}</small>
        </div>
      </div>
      <div *ngIf="!loading && !logs.length" class="empty-state">No scrape logs found.</div>
    </section>
  `,
  styles: [
    `
      .admin-hero { padding: 26px; display: flex; justify-content: space-between; gap: 18px; align-items: center; }
      .admin-hero h1 { margin: 8px 0 10px; font-size: clamp(32px, 5vw, 62px); line-height: 1; overflow-wrap: anywhere; }
      .admin-hero p { max-width: 680px; margin: 0; }
      .hero-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 10px; }
      .danger-btn { border-color: color-mix(in srgb, var(--danger) 42%, var(--line)); color: var(--text); background: color-mix(in srgb, var(--danger) 16%, var(--surface-elevated)); }
      .danger-btn:hover { border-color: color-mix(in srgb, var(--danger) 70%, var(--line)); background: color-mix(in srgb, var(--danger) 24%, var(--surface-elevated)); }
      .notice-state { padding: 14px 18px; border-radius: 18px; border: 1px solid var(--line); background: var(--control-bg); color: var(--green); font-weight: 850; margin: 18px 0 0; }
      .progress-card { padding: 20px; margin: 22px 0; }
      .progress-head { display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; }
      .progress-head h2 { margin: 7px 0 8px; font-size: clamp(24px, 4vw, 34px); }
      .progress-head p { margin: 0; color: var(--muted); line-height: 1.45; }
      .progress-head > strong { color: var(--cyan); font-size: clamp(38px, 7vw, 64px); line-height: 1; }
      .progress-track { height: 12px; overflow: hidden; border-radius: 999px; background: var(--surface-soft); border: 1px solid var(--line); margin: 18px 0 14px; }
      .progress-track span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(135deg, var(--red), var(--violet), var(--cyan)); transition: width 260ms ease; }
      .progress-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
      .progress-grid span, .current-cinema { min-height: 42px; border-radius: 14px; padding: 10px 12px; background: var(--control-bg); color: var(--muted); font-weight: 800; }
      .progress-grid strong, .current-cinema strong { color: var(--text); }
      .current-cinema { margin-top: 10px; }
      .stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin: 22px 0 36px; }
      .stat { padding: 20px; }
      .stat span { display: block; color: var(--muted); font-weight: 800; }
      .stat strong { display: block; margin-top: 10px; font-size: clamp(34px, 5vw, 56px); line-height: 1; color: var(--cyan); }
      .compact-head { margin-top: 20px; }
      .run-list { display: flex; gap: 10px; overflow-x: auto; padding: 2px 2px 12px; scrollbar-width: thin; }
      .run-chip { flex: 0 0 260px; min-height: 68px; text-align: left; border: 1px solid var(--line); border-radius: 16px; padding: 11px 13px; background: var(--surface); color: var(--text); box-shadow: var(--shadow); cursor: pointer; }
      .run-chip strong, .run-chip span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .run-chip span { margin-top: 5px; color: var(--muted); font-size: 12px; font-weight: 800; text-transform: capitalize; }
      .run-chip.active { border-color: color-mix(in srgb, var(--cyan) 70%, var(--line)); background: color-mix(in srgb, var(--cyan) 10%, var(--surface)); }
      .logs { overflow: hidden; }
      .log-row { display: grid; grid-template-columns: 1.2fr .6fr .45fr 1.8fr; gap: 12px; align-items: center; padding: 14px 16px; border-top: 1px solid var(--line); }
      .log-row:first-child { border-top: 0; }
      .log-row.header { color: var(--muted); font-size: 12px; font-weight: 900; text-transform: uppercase; letter-spacing: .08em; background: var(--surface-soft); }
      .log-row small { color: var(--muted); line-height: 1.35; }
      .status { width: fit-content; min-height: 28px; border-radius: 999px; padding: 5px 10px; font-size: 12px; font-weight: 900; background: var(--control-bg); color: var(--muted); }
      .mobile-label { display: none; }
      .success { color: var(--success); background: color-mix(in srgb, var(--success) 10%, transparent); }
      .empty { color: var(--warning); background: color-mix(in srgb, var(--warning) 11%, transparent); }
      .failed { color: var(--danger); background: color-mix(in srgb, var(--danger) 13%, transparent); }
      @media (max-width: 840px) {
        .admin-hero { align-items: flex-start; flex-direction: column; border-radius: 20px; padding: 18px; }
        .admin-hero h1 { font-size: 32px; line-height: 1.04; }
        .hero-actions { width: 100%; }
        .admin-hero .btn { flex: 1 1 150px; min-height: 44px; }
        .progress-head { flex-direction: column; }
        .progress-grid { grid-template-columns: 1fr 1fr; }
        .stats { grid-template-columns: 1fr; }
        .run-list { display: grid; grid-template-columns: 1fr; overflow: visible; padding-bottom: 0; }
        .run-chip { width: 100%; min-height: 62px; }
        .logs { background: transparent; border: 0; box-shadow: none; backdrop-filter: none; display: grid; gap: 12px; }
        .log-row, .log-row.header { grid-template-columns: 1fr; gap: 10px; border: 1px solid var(--line); border-radius: 18px; background: var(--surface); box-shadow: var(--shadow); }
        .log-row.header { display: none; }
        .log-row strong,
        .log-row span,
        .log-row small {
          min-width: 0;
          overflow-wrap: anywhere;
        }
        .mobile-label {
          display: block;
          margin-bottom: 4px;
          color: var(--faint);
          font-size: 11px;
          font-weight: 900;
          letter-spacing: .08em;
          text-transform: uppercase;
        }
      }
      @media (max-width: 460px) {
        .admin-hero h1 { font-size: 29px; }
        .progress-card { padding: 14px; border-radius: 18px; }
        .progress-grid { grid-template-columns: 1fr; }
        .progress-head > strong { font-size: 42px; }
      }
    `,
  ],
})
export class AdminScrapeComponent implements OnInit, OnDestroy {
  status?: ScrapeStatus;
  runs: ScrapeRun[] = [];
  selectedRunId = '';
  logs: unknown[] = [];
  running = false;
  stopping = false;
  loading = true;
  error = '';
  notice = '';
  private pollTimer?: ReturnType<typeof setInterval>;
  constructor(private readonly admin: AdminService) {}
  ngOnInit() {
    this.load();
    this.pollTimer = setInterval(() => this.refreshWhileRunning(), 3000);
  }
  ngOnDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  }
  load() {
    this.loading = true;
    this.error = '';
    this.admin.status().subscribe({
      next: (status) => {
        this.status = status;
        this.running = status.progress?.status === 'running';
        if (!this.running) this.stopping = false;
        const activeRunId = status.progress?.runId || status.latestSession?.id || '';
        if (!this.selectedRunId && activeRunId) this.selectedRunId = activeRunId;
      },
      error: () => (this.error = 'Could not load scrape status. Make sure the backend is running.'),
    });
    this.admin.runs().subscribe({
      next: (runs) => {
        this.runs = runs;
        const activeRunId = this.status?.progress?.runId || this.status?.latestSession?.id || runs[0]?.id || '';
        if (!this.selectedRunId && activeRunId) this.selectedRunId = activeRunId;
        this.loadLogs();
      },
      error: () => {
        this.error = 'Could not load scrape sessions.';
        this.loadLogs();
      },
    });
  }
  loadLogs() {
    this.admin.logs(this.selectedRunId || undefined).subscribe({
      next: (logs) => { this.logs = logs; this.loading = false; },
      error: () => { this.error = 'Could not load scrape logs.'; this.loading = false; },
    });
  }
  run() {
    this.running = true;
    this.stopping = false;
    this.notice = '';
    this.admin.run().subscribe({
      next: (response) => {
        if (response.alreadyRunning) {
          this.notice = 'A scrape is already running. This request did not start a second scrape.';
        } else {
          this.notice = 'Scrape started. Progress will update automatically.';
          if (response.progress?.runId) this.selectedRunId = response.progress.runId;
        }
        this.load();
      },
      error: () => {
        this.error = 'Manual scrape failed to start.';
        this.running = false;
      },
    });
  }
  stop() {
    this.stopping = true;
    this.notice = '';
    this.error = '';
    this.admin.stop().subscribe({
      next: (response) => {
        this.notice = response.wasRunning
          ? 'Stop requested. The current cinema will finish, then the scrape will stop.'
          : 'No scrape is currently running.';
        this.status = this.status ? { ...this.status, progress: response.progress } : this.status;
        this.running = response.progress?.status === 'running';
        if (!this.running) this.stopping = false;
      },
      error: () => {
        this.error = 'Could not stop the scrape.';
        this.stopping = false;
      },
    });
  }
  value(row: unknown, key: string): string {
    return typeof row === 'object' && row !== null && key in row ? String((row as Record<string, unknown>)[key] ?? '') : '';
  }
  statusClass(status: string): string {
    return ['success', 'empty', 'failed'].includes(status) ? status : '';
  }
  progressPercent(progress: ScrapeStatus['progress']): number {
    if (!progress?.total) return progress?.status === 'completed' ? 100 : 0;
    return Math.min(100, Math.round((progress.completed / progress.total) * 100));
  }
  selectRun(runId: string) {
    this.selectedRunId = runId;
    this.loading = true;
    this.loadLogs();
  }
  runLabel(run: ScrapeRun): string {
    return new Intl.DateTimeFormat('en-IE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(run.startedAt));
  }
  private refreshWhileRunning() {
    if (this.running || this.status?.progress?.status === 'running') {
      this.admin.status().subscribe({
        next: (status) => {
          this.status = status;
          this.running = status.progress?.status === 'running';
          if (!this.running) this.stopping = false;
        },
      });
      this.admin.runs().subscribe((runs) => (this.runs = runs));
      this.admin.logs(this.selectedRunId || this.status?.progress?.runId).subscribe((logs) => (this.logs = logs));
    }
  }
}
